/**
 * ReportsPage — Platform Health + Ops Reporting (FND-013, INT-007).
 *
 * Two views behind tabs:
 *   - Platform Health (GET /admin/platform-health) — per-service golden signals
 *     (status, p95 latency, error rate, RPM, uptime).
 *   - Ops Reporting (GET /reports/ops-kpis) — headline KPI cards, an enrollment
 *     conversion funnel, an AR-aging summary, and a mock embedded-Power-BI
 *     placeholder (the real embed is tenant-RLS-scoped, FND-003).
 *
 * Data via usePlatform(); both views degrade gracefully to a seed on 404.
 */
import { useState, type CSSProperties } from 'react';
import {
  usePlatform,
  type ArBucket,
  type OpsKpi,
  type ServiceHealth,
} from '../hooks/usePlatform';
import { colors, radius, space } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { Toolbar } from '../components/Toolbar';
import { useShellSlot } from '../components/shellContext';
import { useLifecycle } from '../hooks/useLifecycle';

function fmtKpi(k: OpsKpi): string {
  if (k.format === 'currency') {
    return k.value.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  }
  if (k.format === 'percent') return `${Math.round(k.value * 100)}%`;
  return k.value.toLocaleString();
}

function fmtDelta(delta: number | null | undefined): { text: string; color: string } | null {
  if (delta === null || delta === undefined) return null;
  const up = delta >= 0;
  return {
    text: `${up ? '▲' : '▼'} ${Math.abs(Math.round(delta * 100))}%`,
    color: up ? colors.success : colors.danger,
  };
}

function fmtCurrency(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function healthSemantic(status: string): 'success' | 'warning' | 'danger' {
  const s = status.toLowerCase();
  if (s === 'healthy') return 'success';
  if (s === 'degraded') return 'warning';
  return 'danger';
}

type Tab = 'ops' | 'health' | 'lifecycle';

export function ReportsPage() {
  useShellSlot({ breadcrumb: 'Reports' });
  const { health, ops, healthAvailable, opsAvailable, isLoading, error, refresh } = usePlatform();
  const [tab, setTab] = useState<Tab>('ops');

  const funnelMax = ops?.funnel.reduce((m, s) => Math.max(m, s.count), 0) ?? 0;
  const anySeeded = !healthAvailable || !opsAvailable;

  const healthColumns: Column<ServiceHealth>[] = [
    { key: 'service', header: 'Service', render: (s) => <span style={{ fontWeight: 600 }}>{s.service}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (s) => <StatusBadge value={s.status} status={healthSemantic(s.status)} />,
    },
    { key: 'p95', header: 'p95 latency', align: 'right', render: (s) => `${s.latency_p95_ms} ms` },
    {
      key: 'err',
      header: 'Error rate',
      align: 'right',
      render: (s) => (
        <span style={{ color: s.error_rate > 0.02 ? colors.danger : colors.textPrimary }}>
          {(s.error_rate * 100).toFixed(2)}%
        </span>
      ),
    },
    { key: 'rpm', header: 'RPM', align: 'right', render: (s) => s.rpm.toLocaleString() },
    { key: 'uptime', header: 'Uptime', align: 'right', render: (s) => `${s.uptime_pct}%` },
  ];

  return (
    <>
      <PageHeader
        title="Reports & Platform Health"
        subtitle="Operational KPIs, the enrollment conversion funnel, AR aging, and per-service golden signals (INT-007, FND-013). Client-facing Power BI reports enforce row-level security by tenant."
        actions={
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
      />

      {anySeeded && (
        <p style={styles.seedNotice} role="status">
          Showing seeded metrics for surfaces not yet wired. Live telemetry appears automatically
          when the reporting/observability endpoints are online.
        </p>
      )}
      {error && (
        <p style={styles.error} role="alert">
          {error.message}
        </p>
      )}

      <Toolbar style={{ marginBottom: 16 }}>
        <div style={styles.tabs}>
          <TabBtn active={tab === 'ops'} onClick={() => setTab('ops')}>
            Ops reporting
          </TabBtn>
          <TabBtn active={tab === 'health'} onClick={() => setTab('health')}>
            Platform health
          </TabBtn>
          <TabBtn active={tab === 'lifecycle'} onClick={() => setTab('lifecycle')}>
            Patient lifecycle
          </TabBtn>
        </div>
      </Toolbar>

      {tab === 'ops' ? (
        isLoading || !ops ? (
          <Card>
            <LoadingIndicator text="Loading KPIs…" />
          </Card>
        ) : (
          <>
            <div style={styles.kpiStrip}>
              {ops.kpis.map((k) => {
                const delta = fmtDelta(k.delta);
                return (
                  <div key={k.key} style={styles.kpiCard}>
                    <div style={styles.kpiValue}>{fmtKpi(k)}</div>
                    <div style={styles.kpiLabel}>{k.label}</div>
                    {delta && <div style={{ ...styles.kpiDelta, color: delta.color }}>{delta.text}</div>}
                  </div>
                );
              })}
            </div>

            <div style={styles.twoCol}>
              <Card title="Enrollment conversion funnel">
                <div style={styles.funnel}>
                  {ops.funnel.map((stage) => (
                    <div key={stage.stage} style={styles.funnelRow}>
                      <span style={styles.funnelLabel}>{stage.stage}</span>
                      <div style={styles.funnelBarTrack}>
                        <div
                          style={{
                            ...styles.funnelBar,
                            width: funnelMax ? `${(stage.count / funnelMax) * 100}%` : '0%',
                          }}
                        />
                      </div>
                      <span style={styles.funnelCount}>{stage.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card title="Accounts-receivable aging">
                <div style={styles.arSummary}>
                  <div>
                    <div style={styles.arTotal}>{fmtCurrency(ops.ar_summary?.total_outstanding ?? 0)}</div>
                    <div style={styles.kpiLabel}>
                      outstanding · {ops.ar_summary?.open_invoice_count ?? 0} open invoices
                    </div>
                  </div>
                </div>
                <table style={styles.arTable}>
                  <thead>
                    <tr>
                      <th style={styles.arTh}>Bucket</th>
                      <th style={{ ...styles.arTh, textAlign: 'right' }}>Balance</th>
                      <th style={{ ...styles.arTh, textAlign: 'right' }}>Invoices</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ops.ar_summary?.buckets ?? []).map((b: ArBucket) => (
                      <tr key={b.label}>
                        <td style={styles.arTd}>{b.label}</td>
                        <td style={{ ...styles.arTd, textAlign: 'right' }}>{fmtCurrency(b.balance)}</td>
                        <td style={{ ...styles.arTd, textAlign: 'right' }}>{b.invoice_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>

            <Card title="Embedded report (Power BI)" style={{ marginTop: 20 }}>
              <div style={styles.pbiPlaceholder}>
                <div style={styles.pbiIcon} aria-hidden>
                  📊
                </div>
                <div style={{ fontWeight: 600 }}>Power BI embed placeholder</div>
                <div style={styles.muted}>
                  In production this renders a tenant-scoped Power BI report embedded via an
                  RLS-bound embed token (INT-007). The token enforces row-level security by
                  tenant_id so a hospital only sees its own data (FND-003).
                </div>
              </div>
            </Card>
          </>
        )
      ) : tab === 'health' ? (
        <Card title="Per-service golden signals" padded={false}>
          {isLoading || !health ? (
            <div style={{ padding: 24 }}>
              <LoadingIndicator text="Loading platform health…" />
            </div>
          ) : (
            <DataTable
              columns={healthColumns}
              rows={health.services}
              rowKey={(s) => s.service}
              emptyTitle="No services reporting"
            />
          )}
        </Card>
      ) : (
        <LifecycleView />
      )}
    </>
  );
}

/** Patient lifecycle report (REP-001): consent → close timeline + durations. */
function LifecycleView() {
  const { lifecycle, available, isLoading, error, load } = useLifecycle();
  const [caseId, setCaseId] = useState('');

  const handleLoad = (e: React.FormEvent) => {
    e.preventDefault();
    load(caseId || 'PFA-1042');
  };

  const maxDuration = lifecycle ? Math.max(...lifecycle.stage_breakdown.map((s) => s.days), 1) : 1;

  return (
    <>
      <Card title="Patient lifecycle report (REP-001)">
        <form onSubmit={handleLoad} style={styles.lcForm}>
          <input
            type="text"
            value={caseId}
            onChange={(e) => setCaseId(e.target.value)}
            className="input"
            placeholder="Case id (e.g. PFA-1042)"
            aria-label="Case id"
            style={{ flex: 1, maxWidth: 280 }}
          />
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Loading…' : 'Run report'}
          </button>
        </form>
        {error && (
          <p style={styles.error} role="alert">
            {error.message}
          </p>
        )}
        {!lifecycle && !isLoading && (
          <p style={styles.muted}>Enter a case id to trace its journey from consent through close.</p>
        )}
      </Card>

      {lifecycle && (
        <>
          <div style={styles.lcSummaryRow}>
            <div style={styles.lcSummaryCard}>
              <div style={styles.lcSummaryValue}>{lifecycle.duration_days}</div>
              <div style={styles.kpiLabel}>days, consent → close</div>
            </div>
            <div style={styles.lcSummaryCard}>
              <div style={styles.lcSummaryValue}>{lifecycle.case_ref}</div>
              <div style={styles.kpiLabel}>{lifecycle.timeline.length} lifecycle events</div>
            </div>
            {!available && (
              <p style={styles.seedNotice} role="status">
                Representative lifecycle — live data appears once the lifecycle report endpoint is online
                (or for a case in another tenant).
              </p>
            )}
          </div>

          <div style={styles.twoCol}>
            <Card title="Timeline (consent → close)">
              <ol style={styles.lcTimeline}>
                {lifecycle.timeline.map((e, i) => (
                  <li key={`${e.at}-${i}`} style={styles.lcTimelineItem}>
                    <span style={styles.lcDot} aria-hidden />
                    <div style={{ flex: 1 }}>
                      <div style={styles.lcStageHead}>
                        <span style={styles.lcStageLabel}>{e.event}</span>
                        <span style={styles.lcStageDays}>{e.stage.replace(/_/g, ' ')}</span>
                      </div>
                      <div style={styles.muted}>{e.at ? new Date(e.at).toLocaleString() : '—'}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </Card>

            <Card title="Stage-duration breakdown">
              <div style={styles.funnel}>
                {lifecycle.stage_breakdown.map((s) => (
                  <div key={s.stage} style={styles.funnelRow}>
                    <span style={{ ...styles.funnelLabel, textTransform: 'capitalize' }}>
                      {s.stage.replace(/_/g, ' ')}
                    </span>
                    <div style={styles.funnelBarTrack}>
                      <div style={{ ...styles.funnelBar, width: `${(s.days / maxDuration) * 100}%` }} />
                    </div>
                    <span style={styles.funnelCount}>{s.days}d</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}
    </>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button type="button" onClick={onClick} style={{ ...styles.tabBtn, ...(active ? styles.tabBtnActive : null) }}>
      {children}
    </button>
  );
}

const styles: Record<string, CSSProperties> = {
  seedNotice: {
    margin: '0 0 16px',
    padding: '8px 12px',
    background: colors.brandTint,
    color: colors.brandActive,
    borderRadius: radius.sm,
    fontSize: '0.82rem',
  },
  error: { color: colors.danger, fontSize: '0.9rem', margin: '0 0 12px' },
  muted: { color: colors.textSecondary, fontSize: '0.82rem', lineHeight: 1.5 },
  tabs: { display: 'flex', gap: 4 },
  tabBtn: {
    padding: '7px 14px',
    background: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.pill,
    fontSize: '0.85rem',
    fontWeight: 600,
    color: colors.textSecondary,
    cursor: 'pointer',
  },
  tabBtnActive: { background: colors.brandTint, color: colors.brandActive, borderColor: colors.brandTint },
  kpiStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: 12,
    marginBottom: space.lg,
  },
  kpiCard: {
    background: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.md,
    padding: '14px 16px',
  },
  kpiValue: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.1, color: colors.textPrimary },
  kpiLabel: { fontSize: '0.78rem', color: colors.textSecondary, marginTop: 4 },
  kpiDelta: { fontSize: '0.78rem', fontWeight: 600, marginTop: 4 },
  twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 },
  funnel: { display: 'flex', flexDirection: 'column', gap: 10 },
  funnelRow: { display: 'grid', gridTemplateColumns: '150px 1fr 64px', alignItems: 'center', gap: 10 },
  funnelLabel: { fontSize: '0.82rem', color: colors.textSecondary },
  funnelBarTrack: { background: colors.surfaceAlt, borderRadius: radius.sm, height: 20, overflow: 'hidden' },
  funnelBar: { height: '100%', background: colors.action, borderRadius: radius.sm },
  funnelCount: { fontSize: '0.82rem', fontWeight: 600, textAlign: 'right' },
  arSummary: { marginBottom: 12 },
  arTotal: { fontSize: '1.4rem', fontWeight: 700 },
  arTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  arTh: { textAlign: 'left', padding: '6px 8px', borderBottom: `2px solid ${colors.border}`, color: colors.textSecondary, fontWeight: 600, fontSize: '0.76rem' },
  arTd: { padding: '6px 8px', borderBottom: `1px solid ${colors.border}` },
  pbiPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 6,
    padding: '32px 24px',
    background: colors.surface,
    border: `1px dashed ${colors.borderStrong}`,
    borderRadius: radius.md,
    maxWidth: 520,
    margin: '0 auto',
  },
  pbiIcon: { fontSize: '2rem' },
  lcForm: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 },
  lcSummaryRow: { display: 'flex', gap: 12, flexWrap: 'wrap', margin: `${space.lg}px 0` },
  lcSummaryCard: {
    background: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.md,
    padding: '14px 18px',
    minWidth: 140,
  },
  lcSummaryValue: { fontSize: '1.5rem', fontWeight: 800, color: colors.textPrimary, lineHeight: 1.1 },
  lcTimeline: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 14 },
  lcTimelineItem: { display: 'flex', gap: 10 },
  lcDot: { width: 9, height: 9, borderRadius: '50%', background: colors.action, marginTop: 5, flexShrink: 0 },
  lcStageHead: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 },
  lcStageLabel: { fontWeight: 600, fontSize: '0.9rem', color: colors.textPrimary },
  lcStageDays: { fontSize: '0.8rem', fontWeight: 700, color: colors.action },
};
